import React, { useEffect, useState, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import UpIconSVG from "assets/svg/up-icon";
import DownIconSVG from "assets/svg/down-icon";
import { CoverPlaceholderMilestone } from "constants/images";
import Select from "components/select/select";
import { urlSerializer } from "utils/utils";
import moment from "moment";
import _ from "lodash";
import { useTranslation } from "react-i18next";


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Helper: Converts a hex string to an RGB object.
 */
function hexToRgb(hex) {
  hex = hex.replace(/^#/, "");
  let bigint = parseInt(hex, 16);
  let r, g, b;
  if (hex.length === 6) {
    r = (bigint >> 16) & 255;
    g = (bigint >> 8) & 255;
    b = bigint & 255;
  } else if (hex.length === 3) {
    r = (bigint >> 8) & 15;
    g = (bigint >> 4) & 15;
    b = bigint & 15;
    r = r * 17;
    g = g * 17;
    b = b * 17;
  }
  return { r, g, b };
}

/**
 * Helper: Converts an RGB triplet to a hex string.
 */
function rgbToHex(r, g, b) {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

/**
 * Returns a fixed blue color based on rank.
 * Rank 1: #0A4681  
 * Rank 2: #0B5BAB  
 * Rank 3: #0C70D5  
 * Rank 4: #0D85FF  
 * (and so on)
 */
function getFixedRankColor(rank) {
  const base = { r: 10, g: 70, b: 129 }; // Rank 1: #0A4681
  const step = { r: 1, g: 21, b: 42 }; // Increment per rank
  const r = Math.min(base.r + step.r * (rank - 1), 255);
  const g = Math.min(base.g + step.g * (rank - 1), 255);
  const b = Math.min(base.b + step.b * (rank - 1), 255);
  return rgbToHex(r, g, b);
}

/**
 * Scans every media's `all_months` rows across every milestone to find the
 * earliest and latest summary_date in the entire dataset. Used to decide
 * the All Time chart's bucket granularity and label range.
 */
function getAllTimeDateRange(milestoneList) {
  let minDate = null;
  let maxDate = null;

  milestoneList.forEach((milestone) => {
    milestone.media_type.forEach((media) => {
      media.all_months?.forEach((row) => {
        const d = moment(row.summary_date);
        if (!d.isValid()) return;
        if (!minDate || d.isBefore(minDate)) minDate = d.clone();
        if (!maxDate || d.isAfter(maxDate)) maxDate = d.clone();
      });
    });
  });

  return { minDate, maxDate };
}

/**
 * Decides bucket granularity for the All Time chart based on the total
 * span of available data:
 *  - up to 12 months of history -> bucket by month
 *  - up to 36 months -> bucket by quarter
 *  - beyond that -> bucket by year
 */
function getBucketUnit(minDate, maxDate) {
  if (!minDate || !maxDate) return "month";
  const monthsSpan = maxDate.diff(minDate, "months") + 1;

  if (monthsSpan <= 12) return "month";
  if (monthsSpan <= 36) return "quarter";
  return "year";
}

/**
 * Builds the label list + a lookup map (period key -> array index) spanning
 * from minDate to maxDate at the given bucket unit ("month" | "quarter" | "year").
 */
function buildAllTimeLabelsAndBuckets(minDate, maxDate, unit) {
  const labels = [];
  const dateIndexMap = {};

  let cursor = minDate.clone().startOf(unit === "quarter" ? "quarter" : unit);
  const end = maxDate.clone().endOf(unit === "quarter" ? "quarter" : unit);
  let idx = 0;

  while (cursor.isSameOrBefore(end)) {
    let label, key;
    if (unit === "month") {
      label = cursor.format("MMM YYYY");
      key = cursor.format("YYYY-MM");
      cursor.add(1, "month");
    } else if (unit === "quarter") {
      label = `Q${cursor.quarter()} ${cursor.format("YYYY")}`;
      key = `${cursor.format("YYYY")}-Q${cursor.quarter()}`;
      cursor.add(1, "quarter");
    } else {
      label = cursor.format("YYYY");
      key = cursor.format("YYYY");
      cursor.add(1, "year");
    }
    labels.push(label);
    dateIndexMap[key] = idx;
    idx++;
  }

  return { labels, dateIndexMap };
}

/**
 * Returns the bucket key for a given moment date, matching the key format
 * produced by buildAllTimeLabelsAndBuckets for the same unit.
 */
function getBucketKey(date, unit) {
  if (unit === "month") return date.format("YYYY-MM");
  if (unit === "quarter") return `${date.format("YYYY")}-Q${date.quarter()}`;
  return date.format("YYYY");
}

const defaultBarData = {
  labels: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  datasets: [
    {
      label:
        "Night of the Day of the Dawn of the Son of the Bride of the Return",
      data: [
        1000, 1500, 800, 1200, 1000, 1500, 800, 1200, 1000, 1500, 800, 1200,
      ],
      backgroundColor: "#0C6ED1",
    },
    {
      label:
        "Night of the Day of the Dawn of the Son of the Bride of the Return of the Revenge of the Terror of the Attack of the Evil, Mutant, Hellbound, Flesh-Eating Subhumanoid Zombified Living Dead, Part 3",
      data: [
        3200, 2000, 3000, 2500, 3200, 2000, 3000, 2500, 3200, 2000, 3000, 2500,
      ],
      backgroundColor: "#0CAAD1",
    },
    {
      label: "Night of the Day",
      data: [
        800, 500, 1000, 1200, 800, 500, 1000, 1200, 800, 500, 1000, 1200,
      ],
      backgroundColor: "#EED452",
    },
    {
      label: "My Milestone Title",
      data: [500, 350, 200, 400, 500, 350, 200, 400, 500, 350, 200, 400],
      backgroundColor: "#E9756B",
    },
  ],
};

export default function MilestoneClicksGraph({ userData = null }) {
  const {t} = useTranslation();
  const [milestones, setMilestone] = useState([]);
  const [overallTotals, setOverallTotals] = useState({
    todays_total: 0,
    overall7_days_total: 0,
    overall30_days_total: 0,
    overall12_months_total: 0,
    overallTotal: 0,
  });
  const [barData, setBarData] = useState(defaultBarData);
  const [duration, setDuration] = useState("12 months");
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(-1);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const { http } = global.services;

  const onClickMilestone = (id) => {
    const newMilestones = _.clone(milestones);
    const index = milestones.findIndex((milestone) => milestone.id === id);
    newMilestones[index].showButtons = !newMilestones[index].showButtons;
    setMilestone(newMilestones);
  };

  const onChangeMilestone = (e) => {
    // Ensure the selected ID is a number for consistent comparisons with milestone.id
    const val = e && typeof e.value === "string" ? parseInt(e.value, 10) : e?.value;
    setSelectedMilestoneId(val);
  };

  useEffect(() => {
    getMilestonesClicks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSelectedMilestone = () => {
    return milestones.find((m) => m.id === selectedMilestoneId) || { value: -1, label: t("allMilestones") };
  }

  const getMilestonesClicks = async () => {
    const params = new URLSearchParams(window.location.search);
    const milestoneId = params.get("milestone_id");
    if (milestoneId) {
      // URLSearchParams returns strings — coerce to number to match milestone.id
      const idNum = parseInt(milestoneId, 10);
      if (!isNaN(idNum)) setSelectedMilestoneId(idNum);
    }
    goToMilestoneGraph();
    setLoading(true);
    try {
      const url = urlSerializer("get-analytics", {
        user_profile_id: userData.profile.id,
      });
      const { data } = await http.get(url);
      processMilestonesClicks();
      setMilestone(data.milestone);
      setLoading(false);
    } catch (err) {
      console.log("getMilestonesClicks", err);
    } finally {
      setLoading(false);
    }
  };

  const goToMilestoneGraph = () => {
      const el = containerRef.current || document.getElementById("milestone-clicks-graph");
      if (el && typeof el.scrollIntoView === "function") {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (err) {
          el.scrollIntoView();
        }
      }
  }

  useEffect(() => {
    processMilestonesClicks();
  }, [duration, loading, selectedMilestoneId]);

  const processMilestonesClicks = () => {
    // Reset overall totals (aggregate across all milestones)
    const overallTotalsBuffer = {
      todays_total: 0,
      overall7_days_total: 0,
      overall30_days_total: 0,
      overall12_months_total: 0,
      overallTotal: 0,
    };

    // Process each milestone & its media data
    const milestoneList = milestones.map((milestone) => {
      milestone.last7_days_total = 0;
      milestone.last30_days_total = 0;
      milestone.last12_months_total = 0;
      milestone.label = milestone.title;
      milestone.value = milestone.id;

      // initialize per-milestone totals
      milestone.todays_total = 0;
      milestone.overallTotal = 0;
      milestone.media_type = milestone.media_type.map((media) => {
        media.last7_days_total = 0;
        media.last30_days_total = 0;
        media.last12_months_total = 0;
        const overall = media.overall ? media.overall.clicks : 0;
        // per-milestone totals (count today's clicks and overall if present)
        milestone.todays_total += media.todays_click_count || 0;
        milestone.overallTotal += (media.todays_click_count || 0) + overall;

        // accumulate aggregate totals across all milestones
        overallTotalsBuffer.todays_total += media.todays_click_count || 0;
        overallTotalsBuffer.overallTotal += (media.todays_click_count || 0) + overall;

        media.last7_days?.forEach((day) => {
          media.last7_days_total += day.clicks;
          milestone.last7_days_total += day.clicks;
          overallTotalsBuffer.overall7_days_total += day.clicks;
        });
        media.last30_days?.forEach((day) => {
          media.last30_days_total += day.clicks;
          milestone.last30_days_total += day.clicks;
          overallTotalsBuffer.overall30_days_total += day.clicks;
        });
        media.last12_months?.forEach((day) => {
          media.last12_months_total += day.clicks;
          milestone.last12_months_total += day.clicks;
          overallTotalsBuffer.overall12_months_total += day.clicks;
        });
        return media;
      });
      return milestone;
    });

    setMilestone(milestoneList);

    // If a specific milestone is selected, show its totals instead of the aggregate overall totals
    if (selectedMilestoneId && selectedMilestoneId !== -1) {
      const selected = milestoneList.find((m) => m.id === selectedMilestoneId);
      if (selected) {
        setOverallTotals({
          todays_total: selected.todays_total || 0,
          overall7_days_total: selected.last7_days_total || 0,
          overall30_days_total: selected.last30_days_total || 0,
          overall12_months_total: selected.last12_months_total || 0,
          overallTotal: selected.overallTotal || 0,
        });
      } else {
        setOverallTotals(overallTotalsBuffer);
      }
    } else {
      setOverallTotals(overallTotalsBuffer);
    }

    // Build chart labels and datasets based on selected duration
    const labels = [];
    const dateIndexMap = {};
    const datasets = [];

    if (duration === "7 days") {
      for (let i = 7; i > 0; i--) {
        const date = moment().subtract(i, "days").format("MMM D");
        labels.push(date);
        dateIndexMap[date] = 7 - i;
      }
      milestoneList.forEach((milestone) => {
        if (selectedMilestoneId !== -1 && milestone.id !== selectedMilestoneId)
          return;
        const aggregateClicks = new Array(7).fill(0);
        milestone.media_type.forEach((media) => {
          media.last7_days?.forEach((day) => {
            const date = moment(day.summary_date).format("MMM D");
            if (dateIndexMap.hasOwnProperty(date)) {
              const idx = dateIndexMap[date];
              aggregateClicks[idx] += day.clicks;
            }
          });
        });
        datasets.push({
          label: milestone.title,
          data: aggregateClicks,
        });
      });
    } else if (duration === "30 days") {
      for (let i = 30; i > 0; i--) {
        const date = moment().subtract(i, "days").format("MMM D");
        labels.push(date);
        dateIndexMap[date] = 30 - i;
      }
      milestoneList.forEach((milestone) => {
        if (selectedMilestoneId !== -1 && milestone.id !== selectedMilestoneId)
          return;
        const aggregateClicks = new Array(30).fill(0);
        milestone.media_type.forEach((media) => {
          media.last30_days?.forEach((day) => {
            const date = moment(day.summary_date).format("MMM D");
            if (dateIndexMap.hasOwnProperty(date)) {
              const idx = dateIndexMap[date];
              aggregateClicks[idx] += day.clicks;
            }
          });
        });
        datasets.push({
          label: milestone.title,
          data: aggregateClicks,
        });
      });
    } else if (duration === "12 months") {
      for (let i = 11; i >= 0; i--) {
        const date = moment().subtract(i, "months").format("MMM");
        labels.push(date);
        dateIndexMap[date] = 11 - i;
      }
      milestoneList.forEach((milestone) => {
        if (selectedMilestoneId !== -1 && milestone.id !== selectedMilestoneId)
          return;
        const aggregateClicks = new Array(12).fill(0);
        milestone.media_type.forEach((media) => {
          media.last12_months?.forEach((day) => {
            const date = moment(day.summary_date).format("MMM");
            if (dateIndexMap.hasOwnProperty(date)) {
              const idx = dateIndexMap[date];
              aggregateClicks[idx] += day.clicks;
            }
          });
        });
        datasets.push({
          label: milestone.title,
          data: aggregateClicks,
        });
      });
    } else if (duration === "all time") {
      // Build real buckets from the full, unfiltered monthly history
      // (media.all_months — every `summary_type = 'month'` row ever recorded,
      // no date cutoff). Granularity adapts to how much history exists:
      // <=12 months of data -> month buckets, <=36 months -> quarter buckets,
      // otherwise -> year buckets.
      const { minDate, maxDate } = getAllTimeDateRange(milestoneList);

      if (minDate && maxDate) {
        const unit = getBucketUnit(minDate, maxDate);
        const { labels: builtLabels, dateIndexMap: builtMap } =
          buildAllTimeLabelsAndBuckets(minDate, maxDate, unit);

        labels.push(...builtLabels);
        Object.assign(dateIndexMap, builtMap);

        milestoneList.forEach((milestone) => {
          if (selectedMilestoneId !== -1 && milestone.id !== selectedMilestoneId)
            return;
          const aggregateClicks = new Array(builtLabels.length).fill(0);
          milestone.media_type.forEach((media) => {
            media.all_months?.forEach((row) => {
              const key = getBucketKey(moment(row.summary_date), unit);
              if (dateIndexMap.hasOwnProperty(key)) {
                aggregateClicks[dateIndexMap[key]] += row.clicks;
              }
            });
          });
          datasets.push({
            label: milestone.title,
            data: aggregateClicks,
          });
        });
      }
      // If there's no data at all, labels/datasets stay empty and the chart
      // will simply render blank — same behavior as the other durations
      // when a milestone/profile has no clicks yet.
    }

    // --- REORDER DATASETS & ASSIGN FIXED COLORS PER LABEL ---
    if (datasets.length > 0) {
      // 1. Compute overall total for each dataset.
      datasets.forEach((ds) => {
        ds.totalClicks = ds.data.reduce((sum, val) => sum + val, 0);
      });
      // 2. Sort datasets in descending order so that the highest overall is first (drawn at bottom)
      datasets.sort((a, b) => b.totalClicks - a.totalClicks);
      // 3. Initialize a backgroundColor array for each dataset.
      datasets.forEach((ds) => (ds.backgroundColor = new Array(labels.length)));
      // 4. For each label, rank datasets by that day’s clicks and assign fixed rank colors.
      for (let j = 0; j < labels.length; j++) {
        const ranking = datasets.map((ds, i) => ({ i, value: ds.data[j] }));
        ranking.sort((a, b) => b.value - a.value);
        ranking.forEach((item, rankIndex) => {
          const rank = rankIndex + 1;
          const color = getFixedRankColor(rank);
          datasets[item.i].backgroundColor[j] = color;
        });
      }
      // Remove temporary totalClicks property.
      datasets.forEach((ds) => delete ds.totalClicks);
    }

    const barChartData = {
      labels: labels,
      datasets: datasets,
    };

    setBarData(barChartData);
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: function (context) {
          let tooltipEl = document.getElementById("chartjs-tooltip");
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<table></table>";
            document.body.appendChild(tooltipEl);
          }
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }
          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }
          const totalClicks = context.tooltip.dataPoints[0].formattedValue;
          const milestoneTitle = context.tooltip.dataPoints[0].dataset.label;
          if (tooltipModel.body) {
            let innerHtml = `<div style="display: flex; align-items: center; gap:0.7rem; background-color: #000000CC; color: #CCCCCC; padding: 0.5rem; border-radius: 0.5rem; max-width: 500px; font-size: 12px;">
                                <div style="display: flex; flex-direction: column;">
                                  <strong style="font-size: 16px; color: #FFF;">${totalClicks} ${totalClicks < 2 ? "Click" : "Clicks"}</strong>
                                  <div>${milestoneTitle}</div>
                                </div>
                              </div>`;
            tooltipEl.innerHTML = innerHtml;
          }
          const position = context.chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left =
            position.left + window.pageXOffset + tooltipModel.caretX + "px";
          tooltipEl.style.top =
            position.top + window.pageYOffset + tooltipModel.caretY + "px";
          tooltipEl.style.padding =
            tooltipModel.padding + "px " + tooltipModel.padding + "px";
          tooltipEl.style.pointerEvents = "none";
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          fontColor: "#777",
          fontSize: 12,
          fontFamily: 'Inter", serif',
          fontWeight: "regular",
        },
        grid: {
          display: false,
          zeroLineColor: "transparent",
          borderWidth: 0,
        },
      },
      y: {
        stacked: true,
        grid: {
          borderDash: [8, 4],
          color: "#EDEFF5",
          borderWidth: 0,
        },
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };

  // Helper to pick the right total for the current `duration`, used in both
  // the summary cards and the per-milestone / per-button table rows.
  const getTotalForDuration = (entity) => {
    switch (duration) {
      case "7 days":
        return entity.last7_days_total;
      case "30 days":
        return entity.last30_days_total;
      case "12 months":
        return entity.last12_months_total;
      case "all time":
        return entity.overallTotal;
      default:
        return entity.last12_months_total;
    }
  };

  return (
    <div id="milestone-clicks-graph" ref={containerRef} className="mt-6 w-full container-max-width bg-white rounded-md">
      {/* Content */}
      <div className="w-full pt-12.5 pb-10 px-7.5 1md:px-24.5">
        <div className="w-full space-y-12.5 3md:flex 3md:items-center 3md:space-x-12.5 3md:space-y-0">
          <div className="w-full 3md:w-15/20">
            {/* Headers */}
            <div>
              <h1 className="text-lg leading-5.5 text-lightBlack font-semibold">
                {t('milestoneClicks')}
              </h1>
            </div>
            <div className="page-view-heading w-full 2sm:flex 2sm:justify-between 2sm:items-center space">
              <div className="p-1 bg-thBg rounded-md flex items-center gap-2 leading-7 text-sm font-semibold">
                <div
                  onClick={() => setDuration("7 days")}
                  className={
                    duration === "7 days"
                      ? "text-black bg-white px-3.5"
                      : "text-gray-100 px-3.5 cursor-pointer"
                  }
                >
                  {t("7Days")}
                </div>
                <div
                  onClick={() => setDuration("30 days")}
                  className={
                    duration === "30 days"
                      ? "text-black bg-white px-3.5"
                      : "text-gray-100 px-3.5 cursor-pointer"
                  }
                >
                  {t("30Days")}
                </div>
                <div
                  onClick={() => setDuration("12 months")}
                  className={
                    duration === "12 months"
                      ? "text-black bg-white px-3.5"
                      : "text-gray-100 px-3.5 cursor-pointer"
                  }
                >
                  {t("12Months")}
                </div>
                <div
                  onClick={() => setDuration("all time")}
                  className={
                    duration === "all time"
                      ? "text-black bg-white px-3.5"
                      : "text-gray-100 px-3.5 cursor-pointer"
                  }
                >
                  {t("allTime")}
                </div>
              </div>
              <div className="w-full mt-4 3md:w-6/20">
                <Select
                  options={[
                    { value: -1, label: t("allMilestones") },
                    ...milestones.filter((f) =>
                      duration === "7 days"
                        ? f.last7_days_total
                        : duration === "30 days"
                        ? f.last30_days_total
                        : duration === "all time"
                        ? f.overallTotal
                        : f.last12_months_total
                    ),
                  ]}
                  placeholder={t("allMilestones")}
                  onChange={(e) => onChangeMilestone(e)}
                  value={getSelectedMilestone()}
                  height={40}
                  backgroundColor="#F2F2F2"
                  borderColor="#F2F2F2"
                  isDisabled={loading}
                />
              </div>
            </div>
            {/* Graph */}
            <div className="page-view-graph mt-7.5 w-full">
              <Bar options={barOptions} data={barData} />
            </div>
          </div>
          {/* Right-side (summary) */}
          <div className="w-full 3md:w-5/20">
            <div className="w-full gap-y-14 grid 1sm:grid-cols-3 1sm:gap-x-14 1sm:gap-y-0 3md:grid-cols-1 3md:gap-y-14 3md:gap-x-0">
              <div className="2sm:page-view-item w-full text-center 3md:text-left">
                <p className="text-2xl leading-7.4 font-bold tracking-tighter text-lightBlack">
                  {overallTotals.todays_total}
                </p>
                <p className="text-base leading-6 text-darkerGray">
                  {t('buttonClicksToday')}
                </p>
              </div>
              <div className="page-view-item w-full text-center 3md:text-left">
                <p className="text-2xl leading-7.4 font-bold tracking-tighter text-lightBlack">
                  {duration === "7 days"
                    ? overallTotals.overall7_days_total
                    : duration === "30 days"
                    ? overallTotals.overall30_days_total
                    : duration === "all time"
                    ? overallTotals.overallTotal
                    : overallTotals.overall12_months_total}
                </p>
                <p className="text-base leading-6 text-darkerGray">
                  {duration === "all time"
                    ? t('buttonClicksAllTime')
                    : `${t('buttonClicksLast')} ${duration}`}
                </p>
              </div>
              <div className="page-view-item w-full text-center 3md:text-left">
                <p className="text-2xl leading-7.4 font-bold tracking-tighter text-lightBlack">
                  {overallTotals.overallTotal}
                </p>
                <p className="text-base leading-6 text-darkerGray">
                  {t('totalButtonClicks')}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="w-full pb-8.5">
          <div className="w-full mt-9 mb-3">
            <div className="border-b rounded-md border-gray font-semibold bg-lightBlack text-white text-xs p-2 px-3 grid grid-cols-clicks-table">
              <div>#</div>
              <div>{t('milestonesButtons')}</div>
              <div>{t('buttons')}</div>
              <div>{t('totalClicks')}</div>
            </div>
            {milestones
              .filter((f) =>
                duration === "7 days"
                  ? f.last7_days_total
                  : duration === "30 days"
                  ? f.last30_days_total
                  : duration === "all time"
                  ? f.overallTotal
                  : f.last12_months_total
              )
              .sort((a, b) => getTotalForDuration(b) - getTotalForDuration(a))
              .map((data, index) => (
                <div key={index}>
                  <div
                    onClick={() => onClickMilestone(data.id)}
                    className={`cursor-pointer border-b border-gray p-3 font-semibold text-sm grid grid-cols-clicks-table ${
                      data.showButtons ? "bg-whiteBlue" : ""
                    }`}
                  >
                    <div className="h-11 flex items-center justify-between mr-4">
                      {data.showButtons ? (
                        <UpIconSVG className="inline" stroke="#3E8DDD" />
                      ) : (
                        <DownIconSVG className="inline" stroke="#333333" />
                      )}{" "}
                      <span className={data.showButtons ? "text-lightPrimary" : ""}>
                        {index + 1}
                      </span>
                    </div>
                    <div className="h-11 flex items-center gap-2">
                      {data.thumbnail ? (
                        <img
                          src={data.thumbnail.url}
                          className="w-11 h-11 rounded-md flex items-center object-cover"
                          alt=""
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-md flex items-center bg-gray">
                          <img
                            src={CoverPlaceholderMilestone}
                            className="object-contain object-center w-6 m-auto"
                            alt=""
                          />
                        </div>
                      )}
                      <span>{data.title}</span>
                    </div>
                    <div className="h-11 flex items-center font-normal">
                      <p>{data.media_type?.length}</p>
                    </div>
                    <div className="h-11 flex items-center font-normal">
                      <p>{getTotalForDuration(data)}</p>
                    </div>
                  </div>
                  {data.showButtons && (
                    <div className="flex flex-col">
                      {[...data.media_type]
                        .sort((a, b) => getTotalForDuration(b) - getTotalForDuration(a))
                        .map((button, index) => (
                        <div
                          key={index}
                          className="font-semibold px-2 text-sm grid grid-cols-clicks-table-nested"
                        >
                          <div className="h-11 flex items-center justify-between mr-4"></div>
                          <div className="py-8 ml-6 border-b border-gray h-11 flex items-center gap-2">
                            <div className="w-11 h-11 rounded-md flex items-center bg-gray">
                              <img
                                src={
                                  button.url
                                    ? `http://www.google.com/s2/favicons?domain=${button.url}&sz=32`
                                    : CoverPlaceholderMilestone
                                }
                                className="object-contain object-center w-6 m-auto"
                                alt=""
                              />
                            </div>
                            <span>{button.label}</span>
                          </div>
                          <div className="py-8 border-b border-gray h-11 flex items-center font-normal">
                            <p>{getTotalForDuration(button)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
